import { LoaderBar, PageHeader } from 'components'
import { useAuth } from 'context'
import {
    GetConsolidatedReportRuns,
    GetTempleteList,
    GetUserListForGroup,
    useConsolidatedReportSchedule,
} from 'hooks'
import {
    DeleteScheduleModal,
    ScheduleForm,
    TestSendModal,
    FireNowModal,
    ViewRunDetailModal,
} from 'page-components/consolidated-report-email'
import { ConsolidatedToasts } from 'page-components/consolidated-report-email/consolidated-report-toasts'
import {
    canManageConsolidatedReports,
    extractApiError,
    columnsToPayload,
    formToPayload,
    formatFireWhen,
    getApiErrorCode,
    getApiErrorField,
} from 'page-components/consolidated-report-email/consolidated-report-email-utils'
import { ScreenContainer } from 'page-components/consolidated-report-email/consolidated-report-email-styles'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Message, Segment } from 'semantic-ui-react'
import { ConsolidatedReportScheduleService, DefaultText, PaginationType } from 'utility'

const getCompanyTimezone = user =>
    user?.Companies?.[0]?.TimeZone ||
    user?.TimeZone ||
    'Central Standard Time'

export const ConsolidatedReportEmailFormScreen = () => {
    const { id } = useParams()
    const isEdit = Boolean(id)
    const navigate = useNavigate()
    const { user } = useAuth()
    const timezone = getCompanyTimezone(user)

    const scheduleId = isEdit ? Number(id) : null
    const { data: schedule, loading: scheduleLoading, error: loadError } =
        useConsolidatedReportSchedule(scheduleId)

    const { options: templateOptions, data: templateRecords } =
        GetTempleteList(PaginationType.All)
    const { options: userOptions } = GetUserListForGroup(PaginationType.All)

    const {
        data: recentRuns,
        loading: runsLoading,
        refresh: refreshRuns,
    } = GetConsolidatedReportRuns({
        scheduleId,
        pageSize: 10,
        enabled: isEdit,
    })

    const [saving, setSaving] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})
    const [bannerError, setBannerError] = useState('')
    const [testSendOpen, setTestSendOpen] = useState(false)
    const [testSendLoading, setTestSendLoading] = useState(false)
    const [testSendError, setTestSendError] = useState('')
    const [fireNowOpen, setFireNowOpen] = useState(false)
    const [fireNowLoading, setFireNowLoading] = useState(false)
    const [fireNowError, setFireNowError] = useState('')
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    const [selectedRun, setSelectedRun] = useState(null)

    if (!canManageConsolidatedReports(user)) {
        return (
            <Message negative>
                You don&apos;t have permission to manage consolidated reports.
                This feature is available to company admin users only.
            </Message>
        )
    }

    const applyFieldError = err => {
        const field = getApiErrorField(err)
        const message = extractApiError(err)
        const code = getApiErrorCode(err)

        if (code === 'InvalidValue' && field) {
            setFieldErrors({ [field]: message })
            return true
        }
        if (code === 'DuplicateName') {
            setFieldErrors({ ScheduleName: message || 'This name is already in use.' })
            return true
        }
        if (code === 'SaveFailed' && message) {
            setBannerError(message)
            ConsolidatedToasts.saveFailed()
            return true
        }
        if (err?.status === 404) {
            ConsolidatedToasts.scheduleAlreadyDeleted()
            navigate('/app/consolidated-report-email')
            return true
        }
        return false
    }

    const handleSave = async form => {
        setSaving(true)
        setFieldErrors({})
        setBannerError('')
        try {
            const payload = formToPayload(form)
            const result = isEdit
                ? await ConsolidatedReportScheduleService.update(scheduleId, payload)
                : await ConsolidatedReportScheduleService.add(payload)

            const saved = result?.Schedule || result
            const savedId = saved?.ScheduleId || saved?.Id || scheduleId
            if (!savedId) {
                throw new Error('Schedule saved, but no ScheduleId came back for columns/save')
            }
            try {
                await ConsolidatedReportScheduleService.saveColumns(
                    savedId,
                    columnsToPayload(form.columns),
                )
            } catch (columnErr) {
                const detail = extractApiError(columnErr)
                const status = columnErr?.status
                setBannerError(
                    detail
                        ? `Columns save failed${status ? ` (${status})` : ''}: ${detail}`
                        : `Columns save failed${status ? ` — HTTP ${status}` : ''} on User/ConsolidatedReportSchedule/columns/save. The schedule itself was saved.`,
                )
                ConsolidatedToasts.saveFailed()
                return
            }
            const when = saved?.NextRunUtc
                ? formatFireWhen(saved.NextRunUtc, timezone)
                : ''
            if (isEdit) {
                ConsolidatedToasts.scheduleUpdated(when)
            } else {
                ConsolidatedToasts.scheduleSaved(when)
            }
            navigate('/app/consolidated-report-email')
        } catch (err) {
            if (!applyFieldError(err)) {
                setBannerError(
                    extractApiError(err) ||
                        "Couldn't save schedule — server error, please retry",
                )
                ConsolidatedToasts.saveFailed()
            }
        } finally {
            setSaving(false)
        }
    }

    const handleTestSend = async () => {
        try {
            setTestSendLoading(true)
            setTestSendError('')
            const result = await ConsolidatedReportScheduleService.testSend(scheduleId)
            ConsolidatedToasts.testEmailSent({
                email: result?.RecipientEmail,
                reports: result?.ReportsIncluded,
                mode: result?.RenderMode,
            })
            setTestSendOpen(false)
            refreshRuns()
        } catch (err) {
            setTestSendError(extractApiError(err) || DefaultText.Default)
        } finally {
            setTestSendLoading(false)
        }
    }

    const handleFireNow = async reasonText => {
        try {
            setFireNowLoading(true)
            setFireNowError('')
            const result = await ConsolidatedReportScheduleService.fireNow(
                scheduleId,
                reasonText,
            )
            ConsolidatedToasts.digestFired({
                recipients: result?.RecipientCount,
                reports: result?.ReportsIncluded,
                runId: result?.RunId,
                mode: result?.RenderMode,
                status: result?.Status,
            })
            setFireNowOpen(false)
            refreshRuns()
        } catch (err) {
            const msg = extractApiError(err)
            setFireNowError(msg || 'Fire Now failed')
            ConsolidatedToasts.fireNowFailed(msg)
        } finally {
            setFireNowLoading(false)
        }
    }

    const handleDelete = async () => {
        try {
            setDeleteLoading(true)
            setDeleteError('')
            await ConsolidatedReportScheduleService.remove(scheduleId)
            ConsolidatedToasts.scheduleDeleted()
            navigate('/app/consolidated-report-email')
        } catch (err) {
            if (err?.status === 404) {
                ConsolidatedToasts.scheduleAlreadyDeleted()
                navigate('/app/consolidated-report-email')
                return
            }
            setDeleteError(extractApiError(err) || DefaultText.Default)
        } finally {
            setDeleteLoading(false)
        }
    }

    if (isEdit && scheduleLoading) {
        return <LoaderBar content="Loading schedule" />
    }

    if (isEdit && loadError && !schedule) {
        const isNotFound = loadError?.status === 404
        return (
            <Message negative>
                {isNotFound
                    ? 'This schedule was already deleted. Run history is preserved for audit.'
                    : "Couldn't load schedule."}{' '}
                <a href="/app/consolidated-report-email">Return to list</a>
            </Message>
        )
    }

    const onHoldBanner =
        isEdit && schedule?.OnHold
            ? 'This schedule is currently on hold. Saving will not resume it — use the Resume button in the list.'
            : ''

    return (
        <ScreenContainer>
            <PageHeader
                header={
                    isEdit ? 'Edit Consolidated Report Schedule' : 'New Schedule'
                }
            />
            <Segment raised>
                <ScheduleForm
                    initialSchedule={schedule}
                    isEdit={isEdit}
                    loading={saving}
                    fieldErrors={fieldErrors}
                    bannerError={bannerError}
                    onHoldBanner={onHoldBanner}
                    timezone={timezone}
                    templateOptions={templateOptions}
                    templateRecords={templateRecords}
                    userOptions={userOptions}
                    auditorOptions={userOptions}
                    onSubmit={handleSave}
                    onCancel={() => navigate('/app/consolidated-report-email')}
                    onTestSend={() => setTestSendOpen(true)}
                    onFireNow={() => setFireNowOpen(true)}
                    onDelete={() => setDeleteOpen(true)}
                    recentRuns={recentRuns}
                    runsLoading={runsLoading}
                    onRunClick={setSelectedRun}
                />
            </Segment>

            <TestSendModal
                open={testSendOpen}
                onClose={() => {
                    setTestSendOpen(false)
                    setTestSendError('')
                }}
                onConfirm={handleTestSend}
                loading={testSendLoading}
                adminEmail={user?.Email}
                error={testSendError}
            />

            <FireNowModal
                open={fireNowOpen}
                onClose={() => {
                    setFireNowOpen(false)
                    setFireNowError('')
                }}
                onConfirm={handleFireNow}
                loading={fireNowLoading}
                schedule={schedule}
                timezone={timezone}
                error={fireNowError}
            />

            <DeleteScheduleModal
                open={deleteOpen}
                onClose={() => {
                    setDeleteOpen(false)
                    setDeleteError('')
                }}
                onConfirm={handleDelete}
                loading={deleteLoading}
                scheduleName={schedule?.ScheduleName}
                error={deleteError}
            />

            <ViewRunDetailModal
                open={Boolean(selectedRun)}
                onClose={() => setSelectedRun(null)}
                run={selectedRun}
                timezone={timezone}
            />
        </ScreenContainer>
    )
}
